import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  RefreshControl, ActivityIndicator
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const formatCurrency = (val) => `L. ${parseFloat(val || 0).toFixed(2)}`;
const formatFecha = (f) => f ? new Date(f).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const ESTADO_CONFIG = {
  borrador: { color: '#6c757d', bg: '#f8f9fa', icon: 'document-outline', label: 'Borrador' },
  emitido: { color: '#007bff', bg: '#e8f0fe', icon: 'paper-plane-outline', label: 'Emitido' },
  firmado: { color: '#17a2b8', bg: '#d1ecf1', icon: 'finger-print-outline', label: 'Firmado' },
  pagado: { color: '#28a745', bg: '#d4edda', icon: 'checkmark-circle-outline', label: 'Pagado' },
  anulado: { color: '#dc3545', bg: '#f8d7da', icon: 'close-circle-outline', label: 'Anulado' },
};

const VouchersScreen = ({ navigation }) => {
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [vouchers, setVouchers] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState(null);

  const isAdmin = ['admin', 'supervisor', 'gerente'].includes(user?.rol);

  const cargarDatos = async () => {
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;

      const [vouchersData, resumenData] = await Promise.all([
        api.getVouchers(params),
        isAdmin ? api.getVouchersResumen() : Promise.resolve(null),
      ]);

      setVouchers(vouchersData);
      if (resumenData) setResumen(resumenData);
    } catch (error) {
      console.error('Error al cargar vouchers:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      cargarDatos();
    }, [filtroEstado])
  );

  const onRefresh = () => {
    setRefreshing(true);
    cargarDatos();
  };

  const renderResumen = () => {
    if (!resumen || !isAdmin) return null;

    return (
      <View style={styles.resumenContainer}>
        <View style={styles.resumenRow}>
          <View style={[styles.resumenCard, { borderLeftColor: '#007bff' }]}>
            <Text style={styles.resumenLabel}>Pendientes</Text>
            <Text style={[styles.resumenValue, { color: '#007bff' }]}>
              {(resumen.emitidos || 0) + (resumen.firmados || 0)}
            </Text>
          </View>
          <View style={[styles.resumenCard, { borderLeftColor: '#28a745' }]}>
            <Text style={styles.resumenLabel}>Pagados este mes</Text>
            <Text style={[styles.resumenValue, { color: '#28a745' }]}>
              {formatCurrency(resumen.total_pagado_mes)}
            </Text>
          </View>
        </View>
        <View style={[styles.resumenCardFull, { borderLeftColor: '#ffc107' }]}>
          <Text style={styles.resumenLabel}>Total pendiente de pago</Text>
          <Text style={[styles.resumenValue, { color: '#ffc107' }]}>
            {formatCurrency(resumen.total_pendiente_pago)}
          </Text>
        </View>
      </View>
    );
  };

  const renderFiltros = () => (
    <View style={styles.filtrosContainer}>
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        data={[
          { key: null, label: 'Todos' },
          { key: 'emitido', label: 'Emitidos' },
          { key: 'firmado', label: 'Firmados' },
          { key: 'pagado', label: 'Pagados' },
          { key: 'anulado', label: 'Anulados' },
        ]}
        keyExtractor={(item) => item.key || 'all'}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[
              styles.filtroPill,
              filtroEstado === item.key && styles.filtroPillActive,
            ]}
            onPress={() => setFiltroEstado(item.key)}
          >
            <Text style={[
              styles.filtroPillText,
              filtroEstado === item.key && styles.filtroPillTextActive,
            ]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        )}
      />
    </View>
  );

  const renderVoucher = ({ item }) => {
    const est = ESTADO_CONFIG[item.estado] || ESTADO_CONFIG.borrador;

    return (
      <TouchableOpacity
        style={styles.voucherCard}
        onPress={() => navigation.navigate('DetalleVoucher', { voucherId: item.id })}
        activeOpacity={0.7}
      >
        <View style={styles.voucherHeader}>
          <View style={styles.voucherNumero}>
            <Ionicons name="receipt-outline" size={18} color="#007bff" />
            <Text style={styles.voucherNumeroText}>{item.numero_voucher}</Text>
          </View>
          <View style={[styles.estadoBadge, { backgroundColor: est.bg }]}>
            <Ionicons name={est.icon} size={14} color={est.color} />
            <Text style={[styles.estadoText, { color: est.color }]}>{est.label}</Text>
          </View>
        </View>

        <View style={styles.voucherBody}>
          <View style={styles.voucherInfo}>
            <Ionicons name="person-outline" size={16} color="#666" />
            <Text style={styles.voucherInfoText}>{item.empleado_nombre}</Text>
            <View style={styles.rolBadge}>
              <Text style={styles.rolBadgeText}>{item.empleado_rol}</Text>
            </View>
          </View>

          <View style={styles.voucherInfo}>
            <Ionicons name="calendar-outline" size={16} color="#666" />
            <Text style={styles.voucherInfoText}>
              {formatFecha(item.periodo_inicio)} - {formatFecha(item.periodo_fin)}
            </Text>
          </View>
        </View>

        <View style={styles.voucherFooter}>
          <View>
            <Text style={styles.montoLabel}>Ingresos</Text>
            <Text style={styles.montoIngreso}>{formatCurrency(item.total_ingresos)}</Text>
          </View>
          <View>
            <Text style={styles.montoLabel}>Deducciones</Text>
            <Text style={styles.montoDeducciones}>{formatCurrency(item.total_deducciones)}</Text>
          </View>
          <View>
            <Text style={styles.montoLabel}>Neto</Text>
            <Text style={styles.montoNeto}>{formatCurrency(item.monto_neto)}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={styles.loadingText}>Cargando vouchers...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderResumen()}
      {renderFiltros()}

      <FlatList
        data={vouchers}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderVoucher}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#007bff']} />
        }
        contentContainerStyle={vouchers.length === 0 ? styles.emptyContainer : styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Ionicons name="receipt-outline" size={60} color="#ccc" />
            <Text style={styles.emptyTitle}>Sin vouchers</Text>
            <Text style={styles.emptySubtitle}>
              {filtroEstado ? 'No hay vouchers con este filtro' : 'Genera tu primer voucher de pago'}
            </Text>
          </View>
        }
      />

      {['admin', 'supervisor'].includes(user?.rol) && (
        <TouchableOpacity
          style={[styles.fab, { bottom: 20 + insets.bottom }]}
          onPress={() => navigation.navigate('GenerarVoucher')}
          activeOpacity={0.8}
        >
          <Ionicons name="add" size={28} color="#fff" />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },

  resumenContainer: { padding: 16, paddingBottom: 0 },
  resumenRow: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  resumenCard: {
    flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: 14,
    borderLeftWidth: 4, elevation: 2,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3,
  },
  resumenCardFull: {
    backgroundColor: '#fff', borderRadius: 10, padding: 14,
    borderLeftWidth: 4, elevation: 2,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3,
  },
  resumenLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  resumenValue: { fontSize: 18, fontWeight: 'bold', marginTop: 4 },

  filtrosContainer: { paddingHorizontal: 16, paddingVertical: 10 },
  filtroPill: {
    paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20,
    backgroundColor: '#fff', marginRight: 8, borderWidth: 1, borderColor: '#ddd',
  },
  filtroPillActive: { backgroundColor: '#007bff', borderColor: '#007bff' },
  filtroPillText: { fontSize: 13, color: '#666', fontWeight: '500' },
  filtroPillTextActive: { color: '#fff' },

  listContent: { padding: 16, paddingTop: 0 },

  voucherCard: {
    backgroundColor: '#fff', borderRadius: 12, marginBottom: 12,
    elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1, shadowRadius: 3, overflow: 'hidden',
  },
  voucherHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    padding: 14, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0',
  },
  voucherNumero: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  voucherNumeroText: { fontSize: 15, fontWeight: '700', color: '#333' },
  estadoBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12,
  },
  estadoText: { fontSize: 12, fontWeight: '600' },

  voucherBody: { padding: 14, paddingVertical: 10, gap: 6 },
  voucherInfo: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  voucherInfoText: { fontSize: 13, color: '#555', flex: 1 },
  rolBadge: {
    backgroundColor: '#e8f0fe', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8,
  },
  rolBadgeText: { fontSize: 10, color: '#007bff', fontWeight: '600', textTransform: 'uppercase' },

  voucherFooter: {
    flexDirection: 'row', justifyContent: 'space-between', padding: 14,
    paddingTop: 10, backgroundColor: '#fafafa', borderTopWidth: 1, borderTopColor: '#f0f0f0',
  },
  montoLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase', marginBottom: 2 },
  montoIngreso: { fontSize: 14, fontWeight: '700', color: '#28a745' },
  montoDeducciones: { fontSize: 14, fontWeight: '700', color: '#dc3545' },
  montoNeto: { fontSize: 16, fontWeight: '700', color: '#007bff' },

  emptyContainer: { flex: 1, justifyContent: 'center' },
  emptyState: { alignItems: 'center', paddingVertical: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#999', marginTop: 12 },
  emptySubtitle: { fontSize: 13, color: '#bbb', marginTop: 4 },

  fab: {
    position: 'absolute', right: 20, bottom: 20, width: 56, height: 56,
    borderRadius: 28, backgroundColor: '#007bff', justifyContent: 'center',
    alignItems: 'center', elevation: 6,
    shadowColor: '#007bff', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.3, shadowRadius: 6,
  },
});

export default VouchersScreen;
